import React, { useEffect, useState } from "react";
import { Form, Select, InputNumber, Checkbox, Typography, Button, Space, Alert } from "antd";

import { elvis } from "../../Utils";

const { Option } = Select;
const { Title } = Typography;

// 28 standard mechanisms of the Italian Civil Protection "scheda chiese
// A-DC" form, mirroring the server enum
// it.polimi.lode.model.building.ChurchDamageVariant. Labels carry the
// official Mxx code so the surveyor can map back to the paper form;
// English wording is informal — a future i18n pass will localise to
// Italian (the canonical descriptions live in the Java enum's comments).
const CHURCH_MECHANISMS = [
    { value: "FACADE_OVERTURN",           label: "M01 — Facade overturn" },
    { value: "TYMPANUM_OVERTURNING",      label: "M02 — Tympanum overturning" },
    { value: "FACADE_IN_PLANE",           label: "M03 — Facade in-plane mechanism" },
    { value: "NARTHEX",                   label: "M04 — Narthex / portico" },
    { value: "NAVE_LATERAL_OUT_OF_PLANE", label: "M05 — Nave lateral walls (out-of-plane)" },
    { value: "NAVE_LATERAL_IN_PLANE",     label: "M06 — Nave lateral walls (in-plane)" },
    { value: "COLONNADE",                 label: "M07 — Colonnade" },
    { value: "CENTRAL_NAVE_VAULT",        label: "M08 — Central nave vault" },
    { value: "AISLE_VAULT",               label: "M09 — Aisle vault" },
    { value: "TRANSEPT_END_OVERTURN",     label: "M10 — Transept end-wall overturn" },
    { value: "TRANSEPT_IN_PLANE",         label: "M11 — Transept in-plane mechanism" },
    { value: "TRANSEPT_VAULT",            label: "M12 — Transept vault" },
    { value: "TRIUMPHAL_ARCH",            label: "M13 — Triumphal arch" },
    { value: "DOME",                      label: "M14 — Dome" },
    { value: "LANTERN",                   label: "M15 — Lantern" },
    { value: "APSE_WALLS",                label: "M16 — Apse walls" },
    { value: "APSE_IN_PLANE",             label: "M17 — Apse in-plane mechanism" },
    { value: "APSE_VAULT",                label: "M18 — Apse vault" },
    { value: "NAVE_ROOF",                 label: "M19 — Nave roof" },
    { value: "TRANSEPT_ROOF",             label: "M20 — Transept roof" },
    { value: "APSE_ROOF",                 label: "M21 — Apse roof" },
    { value: "CHAPEL_FACADE_OVERTURN",    label: "M22 — Chapel facade overturn" },
    { value: "CHAPEL_IN_PLANE",           label: "M23 — Chapel in-plane mechanism" },
    { value: "CHAPEL_VAULT",              label: "M24 — Chapel vault" },
    { value: "DISCONTINUITY_HAMMERING",   label: "M25 — Discontinuity / hammering" },
    { value: "BELL_TOWER_GLOBAL",         label: "M26 — Bell tower (global)" },
    { value: "TOWER_BODY_DETACHMENT",     label: "M27 — Tower–body detachment" },
    { value: "BELL_CELLA",                label: "M28 — Bell cella" },
    { value: "OTHER",                     label: "Other" },
];

// Italian Civil Protection 0–5 damage scale.
const DAMAGE_SCALES = [
    { value: "ABSENT",      label: "0 — Absent" },
    { value: "LOW",         label: "1 — Low" },
    { value: "MODERATE",    label: "2 — Moderate" },
    { value: "SEVERE",      label: "3 — Severe" },
    { value: "VERY_SEVERE", label: "4 — Very severe" },
    { value: "COLLAPSE",    label: "5 — Collapse" },
];

const DamageChurchForm = props => {
    const [form] = Form.useForm();
    const isEditMode = props.isEditMode || false;

    // The Struttura collassata shortcut switches the form between recording a
    // single mechanism (default) and a single whole-structure collapse. Both
    // attach to the same BuildingComponent server-side; only the discriminator
    // (CHURCH_STRUCTURE vs WHOLE_STRUCTURE) changes. Initialise from the
    // loaded damage so editing an existing whole-structure record opens in
    // shortcut mode.
    const initialMode = props.record.damage?.damageDiscriminator === "WHOLE_STRUCTURE"
        ? "wholeStructure"
        : "mechanism";
    const [mode, setMode] = useState(initialMode);

    // The toggle is only effective on NEW damages. JPA JOINED inheritance
    // can't change a row's subclass via UPDATE, so toggling on an existing
    // CHURCH_STRUCTURE / WHOLE_STRUCTURE damage would silently no-op. Hide
    // the button when editing — surveyors who need to switch should delete
    // and recreate.
    const isNewDamage = !props.record.damage?.id || props.record.damage.id === -1;

    const formItemLayout = {
        labelCol: { sm: { span: 8 } },
        wrapperCol: { sm: { span: 16 } },
    };

    useEffect(() => {
        if (props.record.damage) {
            form.setFieldsValue({
                churchVariant:        props.record.damage.churchVariant,
                damageScale:          props.record.damage.damageScale,
                extensionPercent:     props.record.damage.extensionPercent,
                damageToFoundations:  props.record.damage.damageToFoundations || false,
            });
            // Re-sync mode if the damage's discriminator changed under us
            // (e.g. user reselects a different row in the same form).
            const wantedMode = props.record.damage.damageDiscriminator === "WHOLE_STRUCTURE"
                ? "wholeStructure" : "mechanism";
            if (wantedMode !== mode) setMode(wantedMode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.record.damage, form]);

    const propagate = (nextMode) => {
        if (!isEditMode) return;
        const fieldsError = form.getFieldsError();
        const valid = fieldsError.every(field => !field.errors || field.errors.length === 0);
        const values = form.getFieldsValue();

        const newDamage = { ...props.record };
        newDamage.damage.damageDiscriminator = nextMode === "wholeStructure"
            ? "WHOLE_STRUCTURE" : "CHURCH_STRUCTURE";
        newDamage.damage.damageScale      = elvis(values.damageScale);
        newDamage.damage.extensionPercent = elvis(values.extensionPercent);
        // Mechanism-only fields: always carry them. The server creates a
        // DamageWholeStructure when the discriminator is WHOLE_STRUCTURE,
        // and MapStruct silently drops fields the entity doesn't have.
        newDamage.damage.churchVariant       = elvis(values.churchVariant);
        newDamage.damage.damageToFoundations = values.damageToFoundations || false;
        newDamage.valid = valid;
        props.parentCallback(newDamage);
    };

    const onValuesChange = (changedValues) => {
        form.validateFields(Object.keys(changedValues));
    };

    const onFieldsChange = () => {
        propagate(mode);
    };

    const toggleMode = () => {
        const nextMode = mode === "mechanism" ? "wholeStructure" : "mechanism";
        setMode(nextMode);
        propagate(nextMode);
    };

    return (
        <Form
            {...formItemLayout}
            form={form}
            className="details_form"
            onValuesChange={onValuesChange}
            onFieldsChange={onFieldsChange}
        >
            <Title className="details_form_header"
                title="Damages details"
                subTitle={mode === "wholeStructure" ? "Church — whole-structure collapse" : "Church — mechanism"}
            />

            {isNewDamage && (
                <Space style={{ marginBottom: 16 }}>
                    <Button onClick={toggleMode}>
                        {mode === "mechanism"
                            ? "Struttura collassata (shortcut)"
                            : "Back to mechanism"}
                    </Button>
                </Space>
            )}

            {mode === "wholeStructure" && isNewDamage && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Whole-structure collapse"
                    description="Record a single damage for the whole church instead of filling 28 individual mechanisms. Click 'Back to mechanism' to return to the standard form."
                />
            )}

            {mode === "mechanism" && (
                <Form.Item label="Mechanism" name="churchVariant">
                    <Select allowClear showSearch optionFilterProp="children" disabled={!isEditMode}>
                        {CHURCH_MECHANISMS.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))}
                    </Select>
                </Form.Item>
            )}

            <Form.Item label="Damage scale" name="damageScale">
                <Select allowClear disabled={!isEditMode}>
                    {DAMAGE_SCALES.map(opt => (
                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item label="Extension (%)" name="extensionPercent">
                <InputNumber min={0} max={100} disabled={!isEditMode} />
            </Form.Item>

            {mode === "mechanism" && (
                <Form.Item label="Damage to foundations" name="damageToFoundations" valuePropName="checked">
                    <Checkbox disabled={!isEditMode} />
                </Form.Item>
            )}
        </Form>
    );
};

export default DamageChurchForm;
